/**
 * Clip Path Paper Effect
 * 
 * Dynamically generates torn paper clip path effects for containers.
 * This module creates unique SVG-based clip paths with jagged, torn paper edges
 * that can be applied to any HTML element via the `add-clip-path` class.
 * 
 * Features:
 * - Configuration via data attributes with sensible defaults
 * - Preset intensity levels: subtle, medium (default), dramatic
 * - Support for single or multiple edges per container
 * - Each container gets a unique pattern based on its configuration and position
 * - Automatic responsive handling with debounced resize events
 * - Shopify theme editor support (handles section/block updates)
 * - No external dependencies - pure vanilla JavaScript ES module
 * 
 * Configuration Options:
 * - data-clip-edge: Which edge(s) to clip
 *   - Options: "top", "right", "bottom", "left", "all", or comma-separated (e.g., "top,right")
 *   - Default: "right" (single edge)
 * 
 * - data-clip-intensity: Preset intensity level
 *   - Options: "subtle", "medium", "dramatic"
 *   - Default: "medium"
 *   - subtle: 3-5 segments, 2-5% amplitude (gentle torn effect)
 *   - medium: 5-8 segments, 5-10% amplitude (balanced torn effect)
 *   - dramatic: 8-12 segments, 10-15% amplitude (strong torn effect)
 * 
 * - data-clip-segments: Optional override for number of jagged segments
 *   - Overrides the segment count from intensity preset
 *   - Must be a positive integer
 * 
 * Usage:
 * 
 * <!-- Default (right edge, medium intensity) -->
 * <div class="container add-clip-path"></div>
 * 
 * <!-- Custom edge and intensity -->
 * <div class="container add-clip-path" data-clip-edge="top" data-clip-intensity="dramatic"></div>
 * 
 * <!-- Multiple edges -->
 * <div class="container add-clip-path" data-clip-edge="top,right" data-clip-intensity="subtle"></div>
 * 
 * <!-- All edges -->
 * <div class="container add-clip-path" data-clip-edge="all" data-clip-intensity="medium"></div>
 * 
 * <!-- Custom segment count override -->
 * <div class="container add-clip-path" data-clip-edge="right" data-clip-intensity="medium" data-clip-segments="10"></div>
 * 
 * The script automatically initializes on page load and processes all elements
 * with the `add-clip-path` class. It handles dynamic content updates and
 * recalculates clip paths on window resize.
 */

/**
 * Preset intensity configurations
 */
const INTENSITY_PRESETS = {
  subtle: {
    segments: { min: 3, max: 5 },
    amplitude: { min: 0.02, max: 0.05 },
  },
  medium: {
    segments: { min: 5, max: 8 },
    amplitude: { min: 0.05, max: 0.10 },
  },
  dramatic: {
    segments: { min: 8, max: 12 },
    amplitude: { min: 0.10, max: 0.15 },
  },
};

/**
 * Default configuration
 */
const DEFAULT_CONFIG = {
  edge: 'right',
  intensity: 'medium',
};

/**
 * Generates a simple hash from a string for consistent pseudo-randomness
 */
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

/**
 * Generates a pseudo-random number based on a seed
 */
const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

/**
 * Generates jagged points for a torn paper edge
 */
const generateJaggedPoints = (length, config, seed) => {
  const { segments, amplitude } = config;
  const segmentCount = Math.floor(
    segments.min + seededRandom(seed) * (segments.max - segments.min)
  );
  const points = [];
  const segmentLength = length / segmentCount;

  for (let i = 0; i <= segmentCount; i++) {
    const position = i * segmentLength;
    const amp = amplitude.min + seededRandom(seed + i) * (amplitude.max - amplitude.min);
    const offset = (seededRandom(seed + i + 100) - 0.5) * 2 * amp * length;
    points.push({ position, offset });
  }

  return points;
};

/**
 * Creates SVG path string for a clipped edge (normalized 0-1 coordinates)
 */
const createEdgePath = (edge, config, seed) => {
  const isHorizontal = edge === 'top' || edge === 'bottom';
  const length = 1;
  const points = generateJaggedPoints(length, config, seed);

  let path = '';

  if (edge === 'top') {
    path = `M 0,${Math.max(0, Math.min(1, points[0].offset))} `;
    for (let i = 1; i < points.length; i++) {
      const x = points[i].position;
      const y = Math.max(0, Math.min(1, points[i].offset));
      path += `L ${x},${y} `;
    }
    path += `L 1,0 L 0,0 Z`;
  } else if (edge === 'right') {
    path = `M 1,0 `;
    for (let i = 0; i < points.length; i++) {
      const x = Math.max(0, Math.min(1, 1 + points[i].offset));
      const y = points[i].position;
      path += `L ${x},${y} `;
    }
    path += `L 1,1 L 1,0 Z`;
  } else if (edge === 'bottom') {
    path = `M 0,1 `;
    for (let i = 0; i < points.length; i++) {
      const x = points[i].position;
      const y = Math.max(0, Math.min(1, 1 + points[i].offset));
      path += `L ${x},${y} `;
    }
    path += `L 1,1 L 0,1 Z`;
  } else if (edge === 'left') {
    path = `M 0,0 `;
    for (let i = 0; i < points.length; i++) {
      const x = Math.max(0, Math.min(1, points[i].offset));
      const y = points[i].position;
      path += `L ${x},${y} `;
    }
    path += `L 0,1 L 0,0 Z`;
  }

  return path;
};

/**
 * Creates a combined clip path for multiple edges
 * For multiple edges, creates a single path that clips all requested edges
 */
const createCombinedClipPath = (edges, config, baseSeed) => {
  if (edges.length === 0) {
    return `M 0,0 L 1,0 L 1,1 L 0,1 Z`;
  }

  if (edges.length === 1) {
    const edgeSeed = baseSeed + hashString(edges[0]);
    return createEdgePath(edges[0], config, edgeSeed);
  }

  const topPoints = edges.includes('top')
    ? generateJaggedPoints(1, config, baseSeed + hashString('top'))
    : null;
  const rightPoints = edges.includes('right')
    ? generateJaggedPoints(1, config, baseSeed + hashString('right'))
    : null;
  const bottomPoints = edges.includes('bottom')
    ? generateJaggedPoints(1, config, baseSeed + hashString('bottom'))
    : null;
  const leftPoints = edges.includes('left')
    ? generateJaggedPoints(1, config, baseSeed + hashString('left'))
    : null;

  let path = '';

  if (topPoints) {
    path += `M 0,${Math.max(0, Math.min(1, topPoints[0].offset))} `;
    for (let i = 1; i < topPoints.length; i++) {
      path += `L ${topPoints[i].position},${Math.max(0, Math.min(1, topPoints[i].offset))} `;
    }
    path += `L 1,${rightPoints ? Math.max(0, Math.min(1, rightPoints[0].offset)) : 0} `;
  } else {
    path += `M 0,0 `;
    if (rightPoints) {
      path += `L 1,${Math.max(0, Math.min(1, rightPoints[0].offset))} `;
    } else {
      path += `L 1,0 `;
    }
  }

  if (rightPoints) {
    for (let i = 1; i < rightPoints.length; i++) {
      const x = Math.max(0, Math.min(1, 1 + rightPoints[i].offset));
      const y = rightPoints[i].position;
      path += `L ${x},${y} `;
    }
    path += `L ${leftPoints ? Math.max(0, Math.min(1, leftPoints[leftPoints.length - 1].offset)) : 1},1 `;
  } else {
    path += `L 1,${bottomPoints ? Math.max(0, Math.min(1, 1 + bottomPoints[0].offset)) : 1} `;
  }

  if (bottomPoints) {
    for (let i = bottomPoints.length - 2; i >= 0; i--) {
      const x = bottomPoints[i].position;
      const y = Math.max(0, Math.min(1, 1 + bottomPoints[i].offset));
      path += `L ${x},${y} `;
    }
    path += `L ${leftPoints ? Math.max(0, Math.min(1, leftPoints[0].offset)) : 0},1 `;
  } else {
    path += `L ${leftPoints ? Math.max(0, Math.min(1, leftPoints[leftPoints.length - 1].offset)) : 0},1 `;
  }

  if (leftPoints) {
    for (let i = leftPoints.length - 2; i >= 0; i--) {
      const x = Math.max(0, Math.min(1, leftPoints[i].offset));
      const y = leftPoints[i].position;
      path += `L ${x},${y} `;
    }
    path += `Z`;
  } else {
    path += `L 0,${topPoints ? Math.max(0, Math.min(1, topPoints[0].offset)) : 0} Z`;
  }

  return path;
};

/**
 * Gets or creates the SVG defs element for clip paths
 */
const getSvgDefs = () => {
  let svg = document.getElementById('clip-path-paper-svg');
  if (!svg) {
    svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'clip-path-paper-svg';
    svg.setAttribute('style', 'position: absolute; width: 0; height: 0;');
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.appendChild(defs);
    document.body.appendChild(svg);
  }
  return svg.querySelector('defs');
};

/**
 * Applies clip path to an element
 */
const applyClipPath = (element) => {
  const edgeAttr = element.dataset.clipEdge || DEFAULT_CONFIG.edge;
  const intensityAttr = element.dataset.clipIntensity || DEFAULT_CONFIG.intensity;
  const segmentsAttr = element.dataset.clipSegments;

  const edges = edgeAttr === 'all' 
    ? ['top', 'right', 'bottom', 'left']
    : edgeAttr.split(',').map(e => e.trim()).filter(Boolean);

  if (edges.length === 0) {
    edges.push(DEFAULT_CONFIG.edge);
  }

  const intensity = INTENSITY_PRESETS[intensityAttr] || INTENSITY_PRESETS[DEFAULT_CONFIG.intensity];
  const config = segmentsAttr 
    ? { segments: { min: parseInt(segmentsAttr), max: parseInt(segmentsAttr) }, amplitude: intensity.amplitude }
    : intensity;

  const elementId = element.id || `clip-path-${hashString(element.outerHTML)}`;
  const elementIndex = Array.from(document.querySelectorAll('.add-clip-path')).indexOf(element);
  const baseSeed = hashString(elementId + elementIndex.toString());

  const defs = getSvgDefs();
  const clipPathId = `clip-path-${hashString(elementId + edges.join('-') + intensityAttr)}`;

  let clipPath = document.getElementById(clipPathId);
  if (clipPath) {
    clipPath.remove();
  }

  clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
  clipPath.id = clipPathId;
  clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const pathString = createCombinedClipPath(edges, config, baseSeed);
  path.setAttribute('d', pathString);
  clipPath.appendChild(path);
  defs.appendChild(clipPath);

  element.style.clipPath = `url(#${clipPathId})`;
  element.dataset.clipPathApplied = 'true';
};

/**
 * Debounce function for resize events
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Initializes clip paths for all matching elements
 */
const initClipPaths = () => {
  const elements = document.querySelectorAll('.add-clip-path:not([data-clip-path-applied])');
  for (const element of elements) {
    if (element.offsetWidth > 0 && element.offsetHeight > 0) {
      applyClipPath(element);
    }
  }
};

/**
 * Recalculates clip paths on resize
 */
const handleResize = debounce(() => {
  const elements = document.querySelectorAll('.add-clip-path[data-clip-path-applied]');
  for (const element of elements) {
    element.removeAttribute('data-clip-path-applied');
    element.style.clipPath = '';
  }
  initClipPaths();
}, 150);

/**
 * Handles theme editor updates
 */
const handleThemeEditorUpdates = () => {
  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', () => {
      initClipPaths();
    });
    document.addEventListener('shopify:section:reorder', () => {
      initClipPaths();
    });
    document.addEventListener('shopify:block:select', () => {
      initClipPaths();
    });
  }
};

/**
 * Initialization
 */
const init = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initClipPaths();
      handleThemeEditorUpdates();
    });
  } else {
    initClipPaths();
    handleThemeEditorUpdates();
  }

  window.addEventListener('resize', handleResize);

  if (window.Shopify?.designMode) {
    const observer = new MutationObserver(() => {
      initClipPaths();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
};

init();
